import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Sparkles, 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  MessageSquare,
  Send,
  HelpCircle,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme confidence badge component (moved outside to prevent re-renders)
const ThemeConfidenceBadge = ({ confidence }) => {
  const configs = {
    high: {
      icon: ShieldCheck,
      label: 'Theme Mapped',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-700',
      iconColor: 'text-emerald-600',
    },
    medium: {
      icon: ShieldQuestion,
      label: 'Partial Match',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      iconColor: 'text-amber-600',
    },
    low: {
      icon: ShieldAlert,
      label: 'Generic Mode',
      bgColor: 'bg-zinc-100',
      textColor: 'text-zinc-600',
      iconColor: 'text-zinc-500',
    },
  };
  const config = configs[confidence] || configs.low;
  const Icon = config.icon;
  
  return (
    <span 
      className={`px-2 py-1 ${config.bgColor} ${config.textColor} text-xs rounded-full flex items-center gap-1`}
      title={`Theme research confidence: ${(confidence || 'low').toUpperCase()}`}
      data-testid="theme-confidence-badge"
    >
      <Icon className={`w-3 h-3 ${config.iconColor}`} />
      {config.label}
    </span>
  );
};

/**
 * ContentTailoringPanel - Chat-based content tailoring interface
 * 
 * Features:
 * - Start tailoring sweep button
 * - Progress indicator with task count
 * - Pending questions displayed as chat messages
 * - Answer input with structured references
 * - Apply tailored content button
 */
const ContentTailoringPanel = ({ 
  designId, 
  projectId,
  onContentUpdated,
  className = '' 
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submittingAnswer, setSubmittingAnswer] = useState(null);

  // Fetch current tailoring status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/designs/${designId}/tailor/status`,
        { withCredentials: true }
      );
      if (response.data.success && response.data.has_job) {
        setStatus(response.data);
      } else {
        setStatus(null);
      }
    } catch (error) {
      console.error('Failed to fetch tailoring status:', error);
    }
  }, [designId]);

  // Poll for status updates when job is in progress or waiting for input
  useEffect(() => {
    if (status?.status === 'in_progress' || status?.status === 'waiting_for_input') {
      const interval = setInterval(fetchStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [status?.status, fetchStatus]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Start tailoring sweep
  const startTailoring = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/designs/${designId}/tailor`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setStatus(response.data);
        // Show what facts were pre-loaded from previous sessions
        const loadedFacts = response.data.known_facts_loaded || [];
        if (loadedFacts.length > 0) {
          toast.success(`Reusing ${loadedFacts.length} saved fact(s): ${loadedFacts.join(', ').replace(/_/g, ' ')}`);
        } else {
          toast.success('Content tailoring started! Processing in background...');
        }
        
        // If job started in background, immediately start polling
        if (response.data.status === 'in_progress') {
          // Trigger immediate status check after short delay
          setTimeout(fetchStatus, 1500);
        }
      } else {
        toast.error('Failed to start tailoring');
      }
    } catch (error) {
      console.error('Failed to start tailoring:', error);
      toast.error('Failed to start content tailoring');
    } finally {
      setLoading(false);
    }
  };

  // Answer a pending question
  const answerQuestion = async (questionId) => {
    const answer = answers[questionId];
    if (!answer?.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    setSubmittingAnswer(questionId);
    try {
      const response = await axios.post(
        `${API_URL}/api/designs/${designId}/tailor/answer`,
        { question_id: questionId, answer: answer.trim() },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setStatus(response.data);
        setAnswers(prev => ({ ...prev, [questionId]: '' }));
        // Show confirmation that fact was saved for future
        const factSaved = response.data.fact_saved;
        if (factSaved) {
          toast.success(`Saved ${factSaved.replace(/_/g, ' ')} for future designs`);
        } else {
          toast.success('Answer recorded, processing continued');
        }
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
    } finally {
      setSubmittingAnswer(null);
    }
  };

  // Apply tailored content to WordPress
  const applyContent = async () => {
    setApplying(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/designs/${designId}/tailor/apply`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`Applied ${response.data.tasks_applied} tailored elements`);
        if (onContentUpdated) onContentUpdated();
      } else {
        toast.error(response.data.message || 'Failed to apply content');
      }
    } catch (error) {
      console.error('Failed to apply content:', error);
      toast.error('Failed to apply tailored content');
    } finally {
      setApplying(false);
    }
  };

  // Render start state (no job)
  if (!status) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-semibold text-zinc-900 mb-2">Tailor Content</h3>
          <p className="text-sm text-zinc-600 mb-4">
            Automatically customize every element on your site to match your business.
          </p>
          <Button
            onClick={startTailoring}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="start-tailoring-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Start Content Tailoring
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const progressPercent = status.progress_percent || 0;
  const pendingQuestions = status.pending_questions || [];
  const isWaiting = status.status === 'waiting_for_input';
  const isCompleted = status.status === 'completed';
  const isInProgress = status.status === 'in_progress';
  const themeConfidence = status.theme_confidence || 'low';

  return (
    <div className={`p-4 space-y-4 ${className}`} data-testid="content-tailoring-panel">
      {/* Progress Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-zinc-700">
            {isCompleted ? 'Tailoring Complete' : isWaiting ? 'Waiting for Input' : 'Tailoring in Progress'}
          </span>
          <span className="text-zinc-500">
            {status.completed_tasks}/{status.total_tasks} elements
          </span>
        </div>
        <Progress value={progressPercent} className="h-2" />
        
        {/* Status badges */}
        <div className="flex gap-2 flex-wrap">
          {/* Theme Confidence Badge */}
          <ThemeConfidenceBadge confidence={themeConfidence} />
          
          {status.completed_tasks > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {status.completed_tasks} done
            </span>
          )}
          {status.blocked_tasks > 0 && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
              <HelpCircle className="w-3 h-3" />
              {status.blocked_tasks} need info
            </span>
          )}
          {status.failed_tasks > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {status.failed_tasks} failed
            </span>
          )}
        </div>
      </div>

      {/* Pending Questions */}
      {pendingQuestions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
            <MessageSquare className="w-4 h-4" />
            Questions ({pendingQuestions.length})
          </div>
          
          {pendingQuestions.map((q) => (
            <div 
              key={q.question_id} 
              className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
              data-testid={`question-${q.question_id}`}
            >
              <p className="text-sm text-amber-900 mb-2 font-medium">
                {q.question}
              </p>
              <p className="text-xs text-amber-700 mb-2">
                This will update {q.blocks_tasks?.length || 1} element(s) across your site.
              </p>
              <div className="flex gap-2">
                <Input
                  value={answers[q.question_id] || ''}
                  onChange={(e) => setAnswers(prev => ({ 
                    ...prev, 
                    [q.question_id]: e.target.value 
                  }))}
                  placeholder="Your answer..."
                  className="flex-1 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      answerQuestion(q.question_id);
                    }
                  }}
                  data-testid={`answer-input-${q.question_id}`}
                />
                <Button
                  size="sm"
                  onClick={() => answerQuestion(q.question_id)}
                  disabled={submittingAnswer === q.question_id || !answers[q.question_id]?.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid={`submit-answer-${q.question_id}`}
                >
                  {submittingAnswer === q.question_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {isCompleted && status.completed_tasks > 0 && (
          <Button
            onClick={applyContent}
            disabled={applying}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="apply-content-btn"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Apply to Website
              </>
            )}
          </Button>
        )}
        
        {(isInProgress || isWaiting) && (
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Completion message */}
      {isCompleted && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Content tailoring complete!</span>
          </div>
          <p className="text-sm text-green-600 mt-1">
            {status.completed_tasks} elements have been tailored to your business.
            Click &quot;Apply to Website&quot; to update your preview.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContentTailoringPanel;
